import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import { BsEyedropper } from 'react-icons/bs';

type Hsva = { h: number; s: number; v: number; a: number };

type ColorPickerProps = {
  value: string;
  onChange: (hex: string) => void;
  onChangeComplete?: (hex: string) => void;
  label?: React.ReactNode;
  showAlpha?: boolean;
  showEyeDropper?: boolean;
  recentStorageKey?: string;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
  compact?: boolean;
  hideHistory?: boolean;
};

type ColorHistory = {
  manual: string[];
  recent: string[];
};

const DEFAULT_HISTORY: ColorHistory = { manual: [], recent: [] };
const GLOBAL_KEY = 'myclub-colorpicker-v1';

function clamp01(x: number) {
  return Math.min(1, Math.max(0, x));
}

function parseHex(input: string): { r: number; g: number; b: number; a: number } | null {
  if (!input) return null;
  let h = input.trim();
  if (!h.startsWith('#')) h = `#${h}`;
  h = h.slice(1);
  if (![3, 4, 6, 8].includes(h.length)) return null;
  if (h.length === 3 || h.length === 4) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const hasAlpha = h.length === 8;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = hasAlpha ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b) || Number.isNaN(a)) return null;
  return { r, g, b, a };
}

function rgbaToHex(r: number, g: number, b: number, a: number) {
  const toHex = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  const hr = toHex(r);
  const hg = toHex(g);
  const hb = toHex(b);
  const ha = toHex(a * 255);
  if (a >= 0.999) return `#${hr}${hg}${hb}`;
  return `#${hr}${hg}${hb}${ha}`;
}

function hexToHsva(hex: string): Hsva {
  const parsed = parseHex(hex) || { r: 255, g: 0, b: 0, a: 1 };
  const r = parsed.r / 255;
  const g = parsed.g / 255;
  const b = parsed.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const v = max;
  const s = max === 0 ? 0 : d / max;
  if (d !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / d + 2) * 60;
        break;
      case b:
      default:
        h = ((r - g) / d + 4) * 60;
        break;
    }
  }
  if (!Number.isFinite(h)) h = 0;
  return { h, s, v, a: parsed.a };
}

function hsvaToHex(c: Hsva): string {
  const h = ((c.h % 360) + 360) % 360;
  const s = clamp01(c.s);
  const v = clamp01(c.v);
  const a = clamp01(c.a);
  const C = v * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - C;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = C;
    g = X;
    b = 0;
  } else if (h < 120) {
    r = X;
    g = C;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = C;
    b = X;
  } else if (h < 240) {
    r = 0;
    g = X;
    b = C;
  } else if (h < 300) {
    r = X;
    g = 0;
    b = C;
  } else {
    r = C;
    g = 0;
    b = X;
  }
  const rr = (r + m) * 255;
  const gg = (g + m) * 255;
  const bb = (b + m) * 255;
  return rgbaToHex(rr, gg, bb, a);
}

function loadHistory(key?: string): ColorHistory {
  if (typeof window === 'undefined') return DEFAULT_HISTORY;
  try {
    const raw = window.localStorage.getItem(key || GLOBAL_KEY);
    if (!raw) return DEFAULT_HISTORY;
    const parsed = JSON.parse(raw) as ColorHistory;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_HISTORY;
    return {
      manual: Array.isArray(parsed.manual) ? parsed.manual : [],
      recent: Array.isArray(parsed.recent) ? parsed.recent : [],
    };
  } catch {
    return DEFAULT_HISTORY;
  }
}

function saveHistory(history: ColorHistory, key?: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key || GLOBAL_KEY, JSON.stringify(history));
  } catch {
  }
}

function pushRecentColor(history: ColorHistory, hex: string, limit = 10): ColorHistory {
  const color = (hex || '').toLowerCase();
  if (!color) return history;
  const recent = [color, ...history.recent.filter((c) => c !== color)];
  return { ...history, recent: recent.slice(0, limit) };
}

function pushManualColor(history: ColorHistory, hex: string): ColorHistory {
  const color = (hex || '').toLowerCase();
  if (!color) return history;
  if (history.manual.includes(color)) return history;
  return { ...history, manual: [...history.manual, color] };
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  onChangeComplete,
  label,
  showAlpha = false,
  showEyeDropper = true,
  recentStorageKey,
  onInteractionStart,
  onInteractionEnd,
  compact = false,
  hideHistory = false,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.100');
  const subtleText = useColorModeValue('gray.500', 'gray.400');

  const [hsva, setHsva] = useState<Hsva>(() => hexToHsva(value || '#ff0000'));
  const [hex, setHex] = useState<string>(() => hsvaToHex(hsva));
  const [hexInput, setHexInput] = useState<string>(hex.toUpperCase());
  const [isEditingHex, setIsEditingHex] = useState(false);
  const [history, setHistory] = useState<ColorHistory>(() => loadHistory(recentStorageKey));
  const [eyeDropperAvailable, setEyeDropperAvailable] = useState(false);

  const svRef = useRef<HTMLDivElement | null>(null);
  const latestHsvaRef = useRef<Hsva>(hsva);

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).EyeDropper) {
      setEyeDropperAvailable(true);
    }
  }, []);

  useEffect(() => {
    const normalized = hsvaToHex(hexToHsva(value || hex));
    setHsva(hexToHsva(normalized));
    setHex(normalized);
    if (!isEditingHex) setHexInput(normalized.toUpperCase());
  }, [value]);

  useEffect(() => {
    latestHsvaRef.current = hsva;
  }, [hsva]);

  useEffect(() => {
    setHistory(loadHistory(recentStorageKey));
  }, [recentStorageKey]);

  const emitChange = useCallback(
    (nextHsva: Hsva, { complete, addToRecent }: { complete?: boolean; addToRecent?: boolean } = {}) => {
      const nextHex = hsvaToHex(nextHsva);
      setHsva(nextHsva);
      setHex(nextHex);
      if (!isEditingHex) setHexInput(nextHex.toUpperCase());
      onChange(nextHex);
      if (addToRecent) {
        setHistory((prev) => {
          const updated = pushRecentColor(prev, nextHex);
          saveHistory(updated, recentStorageKey);
          return updated;
        });
      }
      if (complete && onChangeComplete) onChangeComplete(nextHex);
    },
    [isEditingHex, onChange, onChangeComplete, recentStorageKey],
  );

  const handleSvPointer = useCallback(
    (clientX: number, clientY: number) => {
      const rect = svRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = clamp01((clientX - rect.left) / rect.width);
      const y = clamp01((clientY - rect.top) / rect.height);
      const s = x;
      const v = 1 - y;
      const base = latestHsvaRef.current;
      emitChange({ ...base, s, v }, { addToRecent: false });
    },
    [emitChange],
  );

  const handleSvPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      onInteractionStart?.();
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      handleSvPointer(e.clientX, e.clientY);
      
      let rafId: number | undefined;
      const move = (ev: PointerEvent) => {
        // Use requestAnimationFrame for smooth 60fps updates
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          handleSvPointer(ev.clientX, ev.clientY);
        });
      };
      const up = (ev: PointerEvent) => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = undefined;
        }
        handleSvPointer(ev.clientX, ev.clientY);
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        const latest = latestHsvaRef.current;
        emitChange(latest, { complete: true, addToRecent: true });
        onInteractionEnd?.();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [handleSvPointer, emitChange, hsva, onInteractionEnd, onInteractionStart],
  );

  const handleHueChange = useCallback((next: number) => {
    emitChange({ ...hsva, h: next }, { addToRecent: false });
  }, [hsva, emitChange]);

  const handleHueChangeStart = () => {
    onInteractionStart?.();
  };

  const handleHueChangeEnd = useCallback((next: number) => {
    emitChange({ ...hsva, h: next }, { complete: true, addToRecent: true });
    onInteractionEnd?.();
  }, [hsva, emitChange, onInteractionEnd]);

  const handleAlphaChange = useCallback((next: number) => {
    emitChange({ ...hsva, a: clamp01(next / 100) }, { addToRecent: false });
  }, [hsva, emitChange]);

  const handleAlphaChangeEnd = useCallback((next: number) => {
    emitChange({ ...hsva, a: clamp01(next / 100) }, { complete: true, addToRecent: true });
  }, [hsva, emitChange]);

  const handleHexInputBlur = () => {
    setIsEditingHex(false);
    const parsed = parseHex(hexInput);
    if (!parsed) {
      setHexInput(hex.toUpperCase());
      return;
    }
    const nextHsva = hexToHsva(hexInput);
    emitChange(nextHsva, { complete: true, addToRecent: true });
  };

  const handleSaveManual = () => {
    setHistory((prev) => {
      const updated = pushManualColor(prev, hex);
      saveHistory(updated, recentStorageKey);
      return updated;
    });
  };

  const handlePickFromScreen = async () => {
    if (typeof window === 'undefined') return;
    if (!eyeDropperAvailable || !(window as any).EyeDropper) return;
    try {
      onInteractionStart?.();
      const EyeDropperCtor = (window as any).EyeDropper;
      const eyeDropper = new EyeDropperCtor();
      const result = await eyeDropper.open();
      const picked = result?.sRGBHex as string | undefined;
      if (picked) {
        const base = latestHsvaRef.current;
        const pickedHsva = hexToHsva(picked);
        const nextHsva = { ...pickedHsva, a: base.a };
        emitChange(nextHsva, { complete: true, addToRecent: true });
      }
    } catch {
    } finally {
      onInteractionEnd?.();
    }
  };

  const svBackground = useMemo(
    () => `linear-gradient(to top, black, transparent), linear-gradient(to right, white, hsl(${hsva.h}, 100%, 50%))`,
    [hsva.h],
  );

  const svPointerStyle = useMemo(() => {
    const x = hsva.s * 100;
    const y = (1 - hsva.v) * 100;
    return { left: `${x}%`, top: `${y}%` };
  }, [hsva.s, hsva.v]);

  const alphaPercent = Math.round(hsva.a * 100);

  const currentHex = hex;

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={compact ? 2 : 3}
      bg={cardBg}
      borderColor={borderColor}
      boxShadow="sm"
    >
      {label ? (
        <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
          {label}
        </Text>
      ) : null}

      <Box
        ref={svRef}
        position="relative"
        borderRadius="lg"
        overflow="hidden"
        height={compact ? '110px' : '140px'}
        cursor="crosshair"
        backgroundImage={svBackground}
        onPointerDown={handleSvPointerDown}
      >
        <Box
          position="absolute"
          width="16px"
          height="16px"
          borderRadius="full"
          borderWidth="2px"
          borderColor="white"
          boxShadow="0 0 0 1px rgba(0,0,0,0.4)"
          transform="translate(-50%, -50%)"
          style={svPointerStyle}
        />
      </Box>

      <Box mt={3}>
        <Slider
          aria-label="Hue"
          min={0}
          max={360}
          value={hsva.h}
          onChange={handleHueChange}
          onChangeStart={handleHueChangeStart}
          onChangeEnd={handleHueChangeEnd}
          size="sm"
        >
          <SliderTrack
            bg="linear-gradient(to right, red, #ff0, #0f0, #0ff, #00f, #f0f, red)"
            h="8px"
            borderRadius="full"
          >
            <SliderFilledTrack bg="transparent" />
          </SliderTrack>
          <SliderThumb boxSize={4} borderWidth="2px" borderColor="white" />
        </Slider>
      </Box>

      {showAlpha && (
        <Box mt={3}>
          <Slider
            aria-label="Opacity"
            min={0}
            max={100}
            value={alphaPercent}
            onChange={handleAlphaChange}
            onChangeEnd={handleAlphaChangeEnd}
            size="sm"
          >
            <SliderTrack
              bg={`linear-gradient(to right, transparent, ${currentHex})`}
              h="8px"
              borderRadius="full"
            >
              <SliderFilledTrack bg="transparent" />
            </SliderTrack>
            <SliderThumb boxSize={4} borderWidth="2px" borderColor="white" />
          </Slider>
        </Box>
      )}

      <HStack spacing={2} mt={3} align="center">
        <Box
          width="32px"
          height="32px"
          borderRadius="full"
          borderWidth="1px"
          borderColor={borderColor}
          bg={currentHex}
        />
        <Input
          value={hexInput}
          onChange={(e) => {
            setIsEditingHex(true);
            setHexInput(e.target.value);
          }}
          onBlur={handleHexInputBlur}
          size="sm"
          fontFamily="mono"
          maxW="130px"
        />
        {showAlpha && (
          <Input
            value={`${alphaPercent}%`}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, '');
              const n = Math.max(0, Math.min(100, parseInt(raw || '0', 10)));
              emitChange({ ...hsva, a: n / 100 }, { addToRecent: false });
            }}
            onBlur={() => handleAlphaChangeEnd(alphaPercent)}
            size="sm"
            maxW="80px"
          />
        )}
        {showEyeDropper && (
          <Tooltip
            label={
              eyeDropperAvailable
                ? 'Vybrat barvu z obrazovky'
                : 'Pipeta není v tomto prohlížeči podporována.'
            }
          >
            <IconButton
              aria-label="Vybrat barvu z obrazovky"
              icon={<BsEyedropper />}
              size="sm"
              variant="outline"
              onClick={eyeDropperAvailable ? handlePickFromScreen : undefined}
              isDisabled={!eyeDropperAvailable}
            />
          </Tooltip>
        )}
      </HStack>

      {!hideHistory && (
        <VStack align="stretch" spacing={2} mt={3}>
        {history.manual.length > 0 && (
          <Box>
            <Text fontSize="xs" mb={1} color={subtleText}>
              Uložené barvy
            </Text>
            <SimpleGrid columns={8} spacing={1}>
              {history.manual.map((c) => (
                <Box
                  key={`manual-${c}`}
                  as="button"
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    emitChange(hexToHsva(c), { complete: true, addToRecent: true });
                  }}
                  width="18px"
                  height="18px"
                  borderRadius="full"
                  borderWidth={currentHex.toLowerCase() === c ? '2px' : '1px'}
                  borderColor={currentHex.toLowerCase() === c ? 'blue.500' : borderColor}
                  bg={c}
                />
              ))}
            </SimpleGrid>
          </Box>
        )}

        {history.recent.length > 0 && (
          <Box>
            <Text fontSize="xs" mb={1} color={subtleText}>
              Poslední barvy
            </Text>
            <SimpleGrid columns={8} spacing={1}>
              {history.recent.map((c) => (
                <Box
                  key={`recent-${c}`}
                  as="button"
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    emitChange(hexToHsva(c), { complete: true, addToRecent: true });
                  }}
                  width="16px"
                  height="16px"
                  borderRadius="full"
                  borderWidth={currentHex.toLowerCase() === c ? '2px' : '1px'}
                  borderColor={currentHex.toLowerCase() === c ? 'blue.500' : borderColor}
                  bg={c}
                />
              ))}
            </SimpleGrid>
          </Box>
        )}

          <Button
            mt={1}
            variant="ghost"
            size="sm"
            onClick={handleSaveManual}
          >
            + Přidat barvu
          </Button>
        </VStack>
      )}
    </Box>
  );
};

export default ColorPicker;
